import dayjs from 'dayjs';

export const ENABLE_TIME_LOGGING_SETTING_ID = 'enable_time_cod';
export const SHIFT_TIMINGS_SETTING_ID = 'shift_timings';
export const DEFAULT_TIME_VALUE = '00:00';

export const TIME_INTERVAL_MINUTES = 30;

export const TIME_OPTIONS = Array.from(
  { length: 1440 / TIME_INTERVAL_MINUTES },
  (_, index) => {
    const totalMinutes = index * TIME_INTERVAL_MINUTES;
    const hour = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minute = String(totalMinutes % 60).padStart(2, '0');

    return `${hour}:${minute}`;
  },
);

export const formatTimeTo12Hour = (value, fallback = DEFAULT_TIME_VALUE) => {
  const normalized = normalizeTimeValue(value, fallback);

  return dayjs(`2000-01-01T${normalized}`).format('hh:mm A');
};

export const normalizeTimeValue = (value, fallback = DEFAULT_TIME_VALUE) => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const match = value.match(/^(\d{2}):(\d{2})/);
  if (!match) {
    return fallback;
  }

  const [, hour, minute] = match;
  if (Number(hour) > 23 || Number(minute) > 59) {
    return fallback;
  }

  return `${hour}:${minute}`;
};

// A bare 'YYYY-MM-DD' string parses as UTC midnight per the ISO/JS Date spec;
// appending a time-of-day forces it to be parsed as local midnight instead.
export const parseLocalDate = (value, fallback = new Date()) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = dayjs(`${value}T00:00:00`);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : dayjs(fallback);
};

const extractShiftDefaultTime = (shift, keys) => {
  const source = shift?.raw ?? shift ?? {};
  const matchKey = keys.find((key) => source?.[key]);

  return normalizeTimeValue(source?.[matchKey], DEFAULT_TIME_VALUE);
};

const findShift = (shiftIdentifier, shifts = []) =>
  shifts.find(
    (shift) =>
      shift.value === shiftIdentifier ||
      shift.label === shiftIdentifier ||
      shift.raw?.uuid === shiftIdentifier ||
      shift.raw?.shift === shiftIdentifier,
  );

export const getShiftTimingsMap = (bunkConfig = {}) => {
  const shiftTimings = bunkConfig?.[SHIFT_TIMINGS_SETTING_ID];
  return shiftTimings && typeof shiftTimings === 'object' ? shiftTimings : {};
};

export const getShiftTimingConfig = (
  bunkConfig = {},
  shiftIdentifier,
  shifts = [],
) => {
  const shift = findShift(shiftIdentifier, shifts);
  const shiftTimings = getShiftTimingsMap(bunkConfig);
  const savedTiming =
    shiftTimings?.[shift?.value] ?? shiftTimings?.[shiftIdentifier] ?? {};

  return {
    start_time: normalizeTimeValue(
      savedTiming?.start_time,
      extractShiftDefaultTime(shift, [
        'start_time',
        'start',
        'opening_time',
        'from_time',
      ]),
    ),
    end_time: normalizeTimeValue(
      savedTiming?.end_time,
      extractShiftDefaultTime(shift, [
        'end_time',
        'end',
        'closing_time',
        'to_time',
      ]),
    ),
  };
};

export const isOvernightShift = (startTime, endTime) => {
  const toMinutes = (value) => {
    const [hour, minute] = normalizeTimeValue(value, DEFAULT_TIME_VALUE)
      .split(':')
      .map(Number);
    return hour * 60 + minute;
  };

  return toMinutes(endTime) < toMinutes(startTime);
};

export const buildDateTimeFromTimeValue = (
  dateValue,
  timeValue,
  fallbackDate = new Date(),
) => {
  const normalizedTime = normalizeTimeValue(timeValue, '');
  if (!normalizedTime) {
    return fallbackDate;
  }

  const [hour, minute] = normalizedTime.split(':').map(Number);
  const baseDate = parseLocalDate(dateValue, fallbackDate);

  return baseDate.hour(hour).minute(minute).second(0).millisecond(0).toDate();
};

export const clampDateTimeToNow = (dateValue) => {
  const value = dayjs(dateValue);

  if (!value.isValid()) {
    return new Date();
  }

  if (value.isAfter(dayjs())) {
    return dayjs().second(0).millisecond(0).toDate();
  }

  return value.toDate();
};
